// filename: gtmReadyAfterHydration.ts 
// Purpose: Emit a GTM-friendly custom event once React initial render/hydration completes. 
// Why: GTM can listen to custom events and fire tags only after this moment.  

declare global {
    interface Window {
        __reactHydrationEventFired__?: boolean;
        dataLayer?: any[];
    }
}

/**
 * Checks if a string looks like a phone number.
 * Supports both IE format (e.g., "01 6649900") and UK format (e.g., "01342 331798")
 */
function isValidPhoneNumber(text: string): boolean {
    if (!text || text.length < 6 || text.length > 20) {
        return false;
    }
    
    // Phone number pattern: digits, spaces, dashes, parentheses
    // Must contain at least 6 digits
    const phonePattern = /^[\d\s\-()]+$/;
    if (!phonePattern.test(text)) {
        return false;
    }
    
    // Count digits - must have at least 6 digits
    const digitCount = (text.match(/\d/g) || []).length;
    return digitCount >= 6;
}

/**
 * Checks if phone numbers are present in the DOM.
 * Phone numbers can appear in:
 * - Navbar: in <a> tags with href="tel:..." or <span> elements
 * - Footer: in <Typography> elements with phone number class names
 * 
 * Supports both IE and UK formats:
 * - IE: ClassName is the phone number itself (e.g., "01 6649900")
 * - UK: ClassName is like "rTapNumber164894", phone number is in text (e.g., "01342 331798")
 */
function arePhoneNumbersInDOM(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }

    // Method 1: Check for tel: links (most reliable indicator)
    // Phone numbers appear in Navbar and Footer as tel: links
    const telLinks = document.querySelectorAll('a[href^="tel:"]');
    if (telLinks.length > 0) {
        for (let i = 0; i < telLinks.length; i++) {
            const link = telLinks[i];
            const text = link.textContent?.trim() || '';
            
            if (isValidPhoneNumber(text)) {
                const style = window.getComputedStyle(link);
                if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
                    return true;
                }
            }
        }
    }

    // Method 2: Check for elements with phone number class names (IE format)
    // IE: ClassName is the phone number itself (e.g., "01 6649900")
    // UK: ClassName is like "rTapNumber164894" (not a phone number)
    const allElements = document.querySelectorAll('*');
    for (let i = 0; i < allElements.length; i++) {
        const element = allElements[i];
        const className = element.className?.toString() || '';
        
        // Check if class name itself is a phone number (IE format)
        if (typeof className === 'string') {
            const classParts = className.split(/\s+/);
            for (const classPart of classParts) {
                if (isValidPhoneNumber(classPart)) {
                    // Verify the element also contains phone number text
                    const text = element.textContent?.trim() || '';
                    if (isValidPhoneNumber(text)) {
                        const style = window.getComputedStyle(element);
                        if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
                            return true;
                        }
                    }
                }
            }
        }
    }

    // Method 3: Check for phone number patterns in visible text content (UK format)
    // UK: Phone numbers are in text content, class names are like "rTapNumber164894"
    // Also works as fallback for IE
    const textElements = document.querySelectorAll('span, a, p, div, h1, h2, h3, h4, h5, h6, button');
    for (let i = 0; i < textElements.length; i++) {
        const element = textElements[i];
        const text = element.textContent?.trim() || '';
        console.log('text :', text);
        
        if (isValidPhoneNumber(text)) {
            const style = window.getComputedStyle(element);
            console.log('style :', style);
            if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
                return true;
            }
        }
    }

    return false;
}

/**
 * Waits for phone numbers to appear in the DOM with a timeout.
 * Uses polling to check for phone numbers.
 */
function waitForPhoneNumbers(
    callback: () => void,
    maxAttempts: number = 50,
    intervalMs: number = 100
): void {
    let attempts = 0;

    const checkInterval = setInterval(() => {
        attempts++;

        if (arePhoneNumbersInDOM()) {
            clearInterval(checkInterval);
            callback();
            return;
        }

        if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            // Fire anyway after timeout to avoid blocking indefinitely
            console.warn('Phone numbers not found in DOM after timeout, firing react_hydrated anyway');
            callback();
        }
    }, intervalMs);
}

/**
 * Emits a CustomEvent and a dataLayer.push event that GTM can listen to.
 * Will only fire once per page load.
 * Waits for phone numbers to be in the DOM before firing.
 */
export function notifyGtmAfterHydration(eventName = 'react_hydrated'): void {
    if (typeof window === 'undefined') return;

    if (window.__reactHydrationEventFired__) return;

    // Wait for phone numbers to be in the DOM before firing
    waitForPhoneNumbers(() => {
    if (window.__reactHydrationEventFired__) return;
    window.__reactHydrationEventFired__ = true;

    // 1) Dispatch a DOM CustomEvent (optional, useful for your own listeners)
    //    Not directly used by GTM, but handy if you want to hook things without dataLayer.
    window.dispatchEvent(new CustomEvent(eventName));

    // 2) Push into GTM dataLayer with "event" key — this is what GTM listens to.
    //    GTM requires "event" exactly; other keys are optional context.
    const payload = {
        event: eventName,
        hydratedAt: Date.now(),
    };

    // Ensure dataLayer exists even if GTM loads later
    if (!window.dataLayer) {
        window.dataLayer = [];
    }
    window.dataLayer.push(payload);
    });
}
